import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  RadioGroup,
  Radio,
  Textarea,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';

import { TReportImpactInput } from '../../state/ChangeV2Store';

const useStyles = makeStyles({
  body: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: '440px' },
});

type TMaterial = 'Yes' | 'No' | 'Unsure';

type TProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: (input: TReportImpactInput) => void;
};

// Report a newly discovered impact (prompt Part 8). A material scope change is
// never decided silently - it triggers a visible reassessment.
export const ReportImpactDialog = ({ open, onClose, onConfirm }: TProps) => {
  const classes = useStyles();
  const [description, setDescription] = useState('');
  const [affectedObject, setAffectedObject] = useState('');
  const [material, setMaterial] = useState<TMaterial>('Unsure');
  const [followUp, setFollowUp] = useState('');

  useEffect(() => {
    if (open) {
      setDescription('');
      setAffectedObject('');
      setMaterial('Unsure');
      setFollowUp('');
    }
  }, [open]);

  const canSubmit = description.trim().length > 0 && affectedObject.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="Close" icon={<Dismiss24Regular />} onClick={onClose} />
              </DialogTrigger>
            }
          >
            Report new impact
          </DialogTitle>
          <DialogContent>
            <div className={classes.body}>
              <Field label="Impact description" required>
                <Textarea
                  value={description}
                  resize="vertical"
                  onChange={(_, data) => setDescription(data.value)}
                  placeholder="What did you discover?"
                />
              </Field>
              <Field label="Affected object" required>
                <Input
                  value={affectedObject}
                  onChange={(_, data) => setAffectedObject(data.value)}
                  placeholder="Document, product, process or supplier"
                />
              </Field>
              <Field label="Material scope change">
                <RadioGroup
                  layout="horizontal"
                  value={material}
                  onChange={(_, data) => setMaterial(data.value as TMaterial)}
                >
                  <Radio value="Yes" label="Yes" />
                  <Radio value="No" label="No" />
                  <Radio value="Unsure" label="Unsure" />
                </RadioGroup>
              </Field>
              <Field label="Suggested follow-up (optional)">
                <Input value={followUp} onChange={(_, data) => setFollowUp(data.value)} />
              </Field>

              {material === 'Yes' ? (
                <MessageBar intent="warning">
                  <MessageBarBody>
                    A material impact marks the change as requiring reassessment, creates a visible blocker and prevents
                    a release-readiness request until it is resolved.
                  </MessageBarBody>
                </MessageBar>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!canSubmit}
              onClick={() =>
                onConfirm({
                  description: description.trim(),
                  affectedObject: affectedObject.trim(),
                  materialScopeChange: material,
                  suggestedFollowUp: followUp.trim() || undefined,
                })
              }
            >
              Report impact
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
